import React from "react";
import styled from "@emotion/styled";
import theme from "../theme";
import { mediaQuery } from "../theme/breakpoints";
import { AspectRatio, Skeleton } from "@chakra-ui/react";
import { Subheading } from "./Subheading";
import moment from "moment";
import { Paragraph } from "./Paragraph";
import { OVERSPILL_MEDIA_QUERY } from "../constants";
import YouTube from "react-youtube";
import { videoId } from "../youtube";

export { videoId };

export interface YouTubeVideoProps {
  url: string;
  title: string;
  date: Date;
  // Passed to innnerHTML of <Paragraph>
  blurb?: string[];
  // If true, the details will appear on the left-hand side before the embed.
  mirrored?: boolean;
  // If true, the margins will overspill the max content width of our page
  overspill?: boolean;
}

const Container = styled.div<{ overspill: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${theme.space[12]};

  ${mediaQuery(theme.breakpoints.md)} {
    flex-direction: row;
    flex-wrap: nowrap;
  }

  ${mediaQuery(OVERSPILL_MEDIA_QUERY)} {
    margin-left: ${(props) =>
      props.overspill ? `-${theme.space[6]}` : "auto"};
    margin-right: ${(props) =>
      props.overspill ? `-${theme.space[6]}` : "auto"};
  }
`;

const StyledVideoContainer = styled.div`
  flex-basis: 60%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: black;

  .youtube-container {
    width: 100%;
    height: 100%;
  }
`;

const StyledDetailsContainer = styled.div`
  flex-basis: 40%;
  background-color: ${theme.colors.palette[100]};
  padding: ${theme.space[4]};

  h4 {
    font-size: ${theme.fontSizes.lg};
  }

  h4 {
    margin: ${theme.space[2]} 0 ${theme.space[2]} ${theme.space[2]};
  }

  p {
    margin-left: ${theme.space[2]};
  }
`;

const StyledVideoWrapper = styled.div<{ loaded: boolean }>`
  visibility: ${(props) => (props.loaded ? "visible" : "hidden")};
  width: 100%;
  height: 100%;

  > div {
    width: 100%;
    height: 100%;
  }
`;

export const YouTubeVideo: React.FC<YouTubeVideoProps> = (props) => {
  const mirrored = props.mirrored ?? false;
  const formattedDate = moment(props.date).format("MMMM YYYY");
  const [loaded, setLoaded] = React.useState(false);

  const video = (
    <StyledVideoContainer>
      <AspectRatio maxWidth={"768px"} ratio={1}>
        <div>
          <Skeleton
            startColor={theme.colors.black}
            endColor={theme.colors.palette[400]}
            speed={1.5}
            isLoaded={loaded}
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              visibility: loaded ? "hidden" : "visible"
            }}
          >
            <span></span>
          </Skeleton>
          <StyledVideoWrapper loaded={loaded}>
            <YouTube
              className={"youtubey"}
              videoId={videoId(props.url) ?? ""}
              opts={{ width: "100%", height: "100%" }}
              onReady={() => setLoaded(true)}
            />
          </StyledVideoWrapper>
        </div>
      </AspectRatio>
    </StyledVideoContainer>
  );

  const details = (
    <StyledDetailsContainer>
      <Subheading>{props.title}</Subheading>
      <h4>{formattedDate}</h4>
      {props.blurb &&
        props.blurb.map((b, idx) => (
          <Paragraph key={`media-blurb-${idx}`} innerHTML={b} />
        ))}
    </StyledDetailsContainer>
  );

  return (
    <Container overspill={props.overspill ?? true}>
      {mirrored ? details : video}
      {mirrored ? video : details}
    </Container>
  );
};
